import { detectHost } from "@repo/api";
import {
  AlertBanner,
  Button,
  ButtonSelect,
  Menu,
  Typography,
} from "@saviynt/design-system";
import cx from "classnames";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  IntegrationsListContent,
  IntegrationsListControls,
} from "../components";
import { filterIntegrationTypesByHost } from "../utils/integrationTypeFilter";
import { AgentforceIntegrationSidePanel } from "../components/agentforce";
import { AWSIntegrationSidePanel } from "../components/aws/AWSIntegrationSidePanel";
import { AzureFoundryIntegrationSidePanel } from "../components/azure-foundry";
import { CopilotStudioIntegrationSidePanel } from "../components/CopilotStudio";
import { GoogleVertexIntegrationSidePanel } from "../components/gcp-vertex";
import {
  EntraIDIntegrationSidePanel,
  OktaIntegrationSidePanel,
} from "../components/IdentitySources";
import { ServiceNowIntegrationSidePanel } from "../components/servicenow";
import { useIntegrationsContext } from "../contexts/IntegrationsContext";
import { useIntegrations } from "../hooks/useIntegrations";
import { useIntegrationsFilters } from "../hooks/useIntegrationsFilters";
import {
  IntegrationTypeCategory,
  useIntegrationTypes,
} from "../hooks/useIntegrationTypes";
import { useResponsiveBreakpoints } from "../hooks/useResponsiveBreakpoints";

function isIspmHost(): boolean {
  return (
    typeof window !== "undefined" &&
    (window as Window & { __MFE_HOST__?: string }).__MFE_HOST__ === "ispm"
  );
}

interface IntegrationsProps {
  onAddIntegration: (category?: IntegrationTypeCategory) => void;
  alert?: {
    type: "success" | "error" | "draft";
    message: string;
  } | null;
  onAlertDismiss?: () => void;
}

export function Integrations({
  onAddIntegration,
  alert,
  onAlertDismiss,
}: IntegrationsProps) {
  const intl = useIntl();
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<
    string | null
  >(null);

  // Fetch integration types using React Query
  const { data: rawIntegrationTypesData = [] } = useIntegrationTypes();

  const integrationTypesData = useMemo(
    () => filterIntegrationTypesByHost(rawIntegrationTypesData, detectHost()),
    [rawIntegrationTypesData],
  );

  // Use filters hook - this manages all filter state
  const {
    connectionSource,
    connectionStatus,
    sortField,
    sortDirection,
    searchValue,
    setSearchValue,
    showFilters,
    toggleFilters,
    connectionSourceOptions,
    connectionStatusOptions,
    sourceDisplayLabel,
    statusDisplayLabel,
    handleConnectionSourceChange,
    handleConnectionStatusChange,
    handleSortChange,
  } = useIntegrationsFilters([], integrationTypesData);

  // Debounced search value - only update after user stops typing for 1 second
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [searchValue]);

  // Fetch integrations using React Query
  // Pass integrationType, connectionStatus, sort, and displayName parameters to filter/sort on backend
  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useIntegrations(
    connectionSource,
    connectionStatus,
    sortField,
    sortDirection,
    debouncedSearchValue,
  );

  // Flatten pages into single array
  const integrations = useMemo(
    () => data?.pages.flatMap((page) => page.integrations) ?? [],
    [data],
  );

  const handleRowClick = (integrationId: string) => {
    setSelectedIntegrationId(integrationId);
    setIsSidePanelOpen(true);
  };

  const handleSidepanelClose = () => {
    setIsSidePanelOpen(false);
    setSelectedIntegrationId(null);
  };

  const selectedIntegrationRawData = useMemo(() => {
    if (!selectedIntegrationId) return null;
    const integration = integrations.find(
      (integration) => integration.id === selectedIntegrationId,
    );
    return integration?.rawData || null;
  }, [integrations, selectedIntegrationId]);

  // Responsive breakpoints
  const { isTabletMediumWidth, isMobileXlargeWidth } =
    useResponsiveBreakpoints();

  // Detect SideNav drawer state from host app (synced via Redux store)
  const { integrationsState } = useIntegrationsContext();
  const isMenuPinned = integrationsState.isMenuPinned;

  const [isAddMenuExpanded, setIsAddMenuExpanded] = useState(false);

  // Close sidepanel if selected integration is removed by filters
  useEffect(() => {
    if (selectedIntegrationId && isSidePanelOpen) {
      const isSelectedIntegrationInList = integrations.some(
        (integration) => integration.id === selectedIntegrationId,
      );
      if (!isSelectedIntegrationInList) {
        setIsSidePanelOpen(false);
        setSelectedIntegrationId(null);
      }
    }
  }, [integrations, selectedIntegrationId, isSidePanelOpen]);

  return (
    <div
      className={cx("Integrations-pageContainer", {
        leftNavPinned: isMenuPinned,
      })}
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `aside#SidePanel.Integrations-sidePanel,aside.SidePanel.Integrations-sidePanel,.Integrations-sidePanel{position:fixed!important;top:10rem!important;right:0!important;width:27rem!important;max-width:27rem!important;min-width:27rem!important;height:calc(100vh - 10rem)!important;z-index:15!important;}.Integrations-sidePanel,.Integrations-sidePanel *{word-break:break-word;overflow-wrap:anywhere;}.Integrations-sidePanel [class*="SidePanel-innerContent"]{display:flex!important;flex-direction:column!important;height:100%!important;position:absolute!important;inset:0!important;overflow:hidden!important;}.Integrations-sidePanel [class*="SidePanel-header"]:first-child{flex-shrink:0!important;}.Integrations-sidePanel [class*="SidePanel-innerContent"]>[class*="SidePanel-layout"],.Integrations-sidePanel [class*="SidePanel-innerContent"]>[class*="ModalSidePanel-layout"]{flex:1 1 auto!important;overflow-y:auto!important;overflow-x:hidden!important;min-height:0!important;}.Integrations-sidePanel [class*="SidePanel-footer"]{flex-shrink:0!important;position:sticky!important;bottom:0!important;background-color:var(--color-background-neutral-subtlest,#fff)!important;border-top:1px solid var(--color-border-secondary-decorative,#f2f4f8)!important;z-index:1!important;}`,
        }}
      />
      {/* Page Header - Sticky */}
      <div
        className="sticky top-0 z-20 flex flex-col pt-10 pb-6 py-6 px-12 border-b
       bg-[var(--color-background-neutral-subtlest,#131d2d)]"
        style={{
          borderBottomColor: "var(--color-border-secondary-subtlest, #293950)",
        }}
      >
        <div className="flex items-center justify-between">
          <Typography kind="h1">
            <FormattedMessage
              id="kyIma2"
              defaultMessage="Integrations"
              description="Integrations page title"
            />
          </Typography>
          {isIspmHost() ? (
            <Menu
              placement="bottom-end"
              shouldCloseMenuOnItemClick
              isMenuExpanded={isAddMenuExpanded}
              setIsExpanded={setIsAddMenuExpanded}
              trigger={
                <ButtonSelect
                  kind="rectangle"
                  size="medium"
                  color="neutral"
                  isOpen={isAddMenuExpanded}
                  label={intl.formatMessage({
                    id: "u0XkVE",
                    defaultMessage: "Add Integration",
                    description: "Button to add a new integration",
                  })}
                />
              }
              options={[
                {
                  label: intl.formatMessage({
                    id: "NYo+us",
                    defaultMessage: "Applications",
                    description: "Add Integration menu option for Applications",
                  }),
                  value: "applications",
                },
                {
                  label: intl.formatMessage({
                    id: "mYCpqz",
                    defaultMessage: "Identity Source",
                    description:
                      "Add Integration menu option for Identity Source",
                  }),
                  value: "identitySources",
                },
              ]}
              onChange={(option: { value: string }) => {
                if (option.value === "identitySources") {
                  // Full page reload required — see "Cross-MFE Navigation" in README.md (TD-001)
                  window.location.assign(
                    "/integrations/universalOnboarding/identitySources",
                  );
                } else {
                  onAddIntegration("application");
                }
              }}
            />
          ) : (
            <Button kind="outlined" onClick={() => onAddIntegration()}>
              <FormattedMessage
                id="u0XkVE"
                defaultMessage="Add Integration"
                description="Button to add a new integration"
              />
            </Button>
          )}
        </div>
      </div>

      {/* Alert Banner - Floating below header, always visible in viewport */}
      {alert && (
        <div className="sticky top-[88px] right-0 px-12 pt-4 pl-10 z-10">
          <AlertBanner
            colorTheme={
              alert.type === "draft"
                ? "Information"
                : alert.type === "success"
                  ? "Success"
                  : "Critical"
            }
            description={alert.message}
            kind="low"
            title={
              alert.type === "draft"
                ? intl.formatMessage({
                    id: "Zr6VbR",
                    defaultMessage: "Integration saved as draft",
                    description: "Draft save alert title",
                  })
                : alert.type === "success"
                  ? intl.formatMessage({
                      id: "dl/S9Z",
                      defaultMessage: "Success",
                      description: "Success alert title",
                    })
                  : intl.formatMessage({
                      id: "zMWw5h",
                      defaultMessage: "Error",
                      description: "Error alert title",
                    })
            }
            onCancel={onAlertDismiss || (() => {})}
            shouldAutoDismiss
            autoDismissDelay={5000}
          />
        </div>
      )}

      <div
        className={`Integrations-contentWrapper flex flex-col flex-1 overflow-hidden min-h-0 ${alert ? "pt-20" : ""}`}
      >
        {!isLoading && !isError && (
          <IntegrationsListControls
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            showFilters={showFilters}
            onToggleFilters={toggleFilters}
            onSortChange={handleSortChange}
            connectionSourceOptions={connectionSourceOptions}
            connectionStatusOptions={connectionStatusOptions}
            sourceDisplayLabel={sourceDisplayLabel}
            statusDisplayLabel={statusDisplayLabel}
            onConnectionSourceChange={handleConnectionSourceChange}
            onConnectionStatusChange={handleConnectionStatusChange}
          />
        )}

        <IntegrationsListContent
          integrations={integrations}
          isLoading={isLoading}
          isError={isError}
          error={error}
          isSidePanelOpen={isSidePanelOpen}
          selectedIntegrationId={selectedIntegrationId}
          isTabletMediumWidth={isTabletMediumWidth}
          isMobileXlargeWidth={isMobileXlargeWidth}
          onRowClick={handleRowClick}
          connectionSource={connectionSource}
          connectionStatus={connectionStatus}
          searchValue={debouncedSearchValue}
          onAddIntegration={onAddIntegration}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onRetry={() => {
            // Focus the search input field after retry
            setTimeout(() => {
              // Try multiple selectors to find the input element
              const searchInput =
                (document.getElementById(
                  "integrations-search-input",
                ) as HTMLInputElement) ||
                (document.querySelector(
                  'input[name="search"]',
                ) as HTMLInputElement) ||
                (document.querySelector(
                  '[data-testid="integrations-search-input"] input',
                ) as HTMLInputElement);
              searchInput?.focus();
            }, 100);
          }}
        />
      </div>

      <IntegrationSidePanelDispatcher
        integration={selectedIntegrationRawData}
        isOpen={isSidePanelOpen}
        onClose={handleSidepanelClose}
      />
    </div>
  );
}

interface IntegrationSidePanelDispatcherProps {
  integration: import("@repo/api").Integration | null;
  isOpen: boolean;
  onClose: () => void;
}

function IntegrationSidePanelDispatcher({
  integration,
  isOpen,
  onClose,
}: IntegrationSidePanelDispatcherProps) {
  const type = integration?.integrationType?.toUpperCase();
  if (typeof window !== "undefined" && type) {
    console.debug("[IntegrationSidePanelDispatcher] integrationType =", type);
  }
  switch (type) {
    case "ENTRAID":
    case "ENTRAIDIS":
      return (
        <EntraIDIntegrationSidePanel
          integration={integration}
          isOpen={isOpen}
          onClose={onClose}
        />
      );
    case "AGENTFORCE":
      return (
        <AgentforceIntegrationSidePanel
          integration={integration}
          isOpen={isOpen}
          onClose={onClose}
        />
      );
    case "AZUREFOUNDRY":
    case "AZUREPLATFORM":
      return (
        <AzureFoundryIntegrationSidePanel
          integration={integration}
          isOpen={isOpen}
          onClose={onClose}
        />
      );
    case "COPILOTSTUDIO":
      return (
        <CopilotStudioIntegrationSidePanel
          integration={integration}
          isOpen={isOpen}
          onClose={onClose}
        />
      );
    case "GCPVERTEX":
    case "GCPVERTEXAI":
    case "GOOGLE_VERTEX_DISCOVERY":
    case "GOOGLEPLATFORM":
      return (
        <GoogleVertexIntegrationSidePanel
          integration={integration}
          isOpen={isOpen}
          onClose={onClose}
        />
      );
    case "SERVICENOWAI":
    case "SERVICENOW":
    case "NOWASSIST":
      return (
        <ServiceNowIntegrationSidePanel
          integration={integration}
          isOpen={isOpen}
          onClose={onClose}
        />
      );
    default:
      if (type?.startsWith("OKTA")) {
        return (
          <OktaIntegrationSidePanel
            integration={integration}
            isOpen={isOpen}
            onClose={onClose}
          />
        );
      }
      return (
        <AWSIntegrationSidePanel
          integration={integration}
          isOpen={isOpen}
          onClose={onClose}
        />
      );
  }
}
